import React, { useCallback, useMemo } from 'react';
import { TMaterialStyles, TShadow } from '../../theme';
import {
  ButtonWithShadow,
  TButtonWithShadow,
} from '../Buttons/ButtonWithShadow';
import TypographyWithShadow from '../Typography/TypographyWithShadow';
import { Loader } from '../Common/Loader';

interface IQuizzAnswerButton {
  answer?: string;
  answerIndex: number;
  selectedAnswerIndex?: number | null;
  rightAnswerIndex?: number | null;
  onClick?: (index: number) => void;
  disabled?: boolean;
  isLoading?: boolean;
}

export const QuizzAnswerButton: React.FC<IQuizzAnswerButton> = ({
  answer,
  answerIndex,
  selectedAnswerIndex,
  rightAnswerIndex,
  disabled,
  onClick,
  isLoading,
}) => {
  const isSelected = useMemo(
    () => selectedAnswerIndex === answerIndex,
    [answerIndex, selectedAnswerIndex],
  );

  const isRight = useMemo(
    () => answerIndex === rightAnswerIndex,
    [answerIndex, rightAnswerIndex],
  );

  const isError = useMemo(
    () =>
      isSelected &&
      rightAnswerIndex !== null &&
      rightAnswerIndex !== selectedAnswerIndex,
    [isSelected, rightAnswerIndex, selectedAnswerIndex],
  );

  const buttonTypeByStatus: TButtonWithShadow = useMemo(() => {
    if (isRight) return 'LIGHT_SUCCESS';
    if (isError) return 'ERROR';
    return 'PRIMARY';
  }, [isError, isRight]);

  const typographyShadowType: TShadow = useMemo(() => {
    if (isRight) return 'SUCCESS';
    if (isError) return 'ERROR';
    return 'DARK';
  }, [isRight, isError]);

  const onAnswerSelect = useCallback(
    () => onClick && onClick(answerIndex),
    [onClick, answerIndex],
  );

  return (
    <ButtonWithShadow
      onClick={onAnswerSelect}
      disabled={disabled}
      type={buttonTypeByStatus}
      sx={materialStyles.quizzAnswerContainer}
    >
      <TypographyWithShadow
        sx={materialStyles.answer}
        shadowType={typographyShadowType}
      >
        {answer}
      </TypographyWithShadow>
      {isSelected && isLoading ? (
        <Loader
          sx={materialStyles.loaderContainer}
          iconSx={materialStyles.loaderIcon}
        />
      ) : null}
    </ButtonWithShadow>
  );
};

const materialStyles: TMaterialStyles = {
  quizzAnswerContainer: {
    borderRadius: '14px',
    py: '18px',
    px: '36px',
    flex: 1,
    transition: '0.2s',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  answer: {
    fontFamily: 'Rubber Duck !important',
    fontSize: '20px',
    lineHeight: '100%',
    textAlign: 'center',
    flex: 1,
  },
  loaderContainer: {
    position: 'absolute',
    right: '6px',
    top: '50%',
    transform: 'translate(0, -50%)',
  },
  loaderIcon: {
    width: '34px',
    height: '34px',
  },
};
